/**
 * Owner: Masa Bayu
 * File: qx-draft-image-note.ts
 * Date:Time: 2026-10-16 01:04
 * Description: Figure description under image ↔ tip figure.caption.
 */

import {
  createQXTextCaptionBlock,
  createQXTextInline,
  type QXTextCaptionBlock,
  type QXTextInline,
} from '@qx/qxw-core';

/** Visible caption under image (legacy __note class still accepted). */
export const DRAFT_FIGURE_NOTE_CLASS = 'qxw-draft-figure__desc';
export const DRAFT_FIGURE_NOTE_EVENT = 'qxw:figure-note';

const FIGURE_CLASS = 'qxw-draft-figure';
const SELECTED_CLASS = 'qxw-draft-figure--selected';

export type DraftFigureNoteSession = {
  readonly quote: string;
  readonly note: string;
};

let noteSession: {
  figure: HTMLElement;
  quote: string;
  note: string;
} | null = null;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function editorRoot(): HTMLElement | null {
  const editor = document.querySelector('.qxw-page__editor');
  return editor instanceof HTMLElement ? editor : null;
}

function selectFigure(editor: HTMLElement, figure: HTMLElement): void {
  editor.querySelectorAll(`.${SELECTED_CLASS}`).forEach((el) => el.classList.remove(SELECTED_CLASS));
  figure.classList.add(SELECTED_CLASS);
}

function resolveFigure(preferred?: HTMLElement | null): HTMLElement | null {
  const editor = editorRoot();
  if (!editor) return null;
  if (preferred && editor.contains(preferred) && preferred.classList.contains(FIGURE_CLASS)) {
    return preferred;
  }
  const selected = editor.querySelector(`.${SELECTED_CLASS}`);
  if (selected instanceof HTMLElement && selected.classList.contains(FIGURE_CLASS)) {
    return selected;
  }
  const last = editor.querySelector(`.${FIGURE_CLASS}`);
  return last instanceof HTMLElement ? last : null;
}

function noteTextFromFigure(figure: HTMLElement): string {
  const note = figure.querySelector('figcaption');
  if (!(note instanceof HTMLElement)) return '';
  return (note.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

function quoteFromFigure(figure: HTMLElement): string {
  const alt = figure.querySelector('img')?.getAttribute('alt')?.trim();
  return alt && alt.length > 0 ? alt : 'Image';
}

/** Plain text from caption inlines (hydrate). */
export function captionPlainFromInlines(inlines: readonly QXTextInline[]): string {
  return inlines
    .map((inline) => (inline.type === 'text' ? inline.text : ''))
    .join('')
    .replace(/\u00a0/g, ' ')
    .trim();
}

/** Tip caption from figure note DOM — empty → omit. */
export function captionBlockFromFigure(
  figure: Element | null | undefined,
): QXTextCaptionBlock | undefined {
  if (!(figure instanceof HTMLElement)) return undefined;
  const text = noteTextFromFigure(figure);
  if (!text) return undefined;
  return createQXTextCaptionBlock([createQXTextInline(text)]);
}

/** Display-only description under image (edit via composer). */
export function htmlDraftFigureNote(captionText: string | undefined): string {
  if (!captionText?.trim()) return '';
  return `<figcaption class="${DRAFT_FIGURE_NOTE_CLASS}" contenteditable="false">${escapeHtml(captionText.trim())}</figcaption>`;
}

/** Keep description display-only under contenteditable=false figure. */
export function normalizeDraftFigureNote(figure: HTMLElement): void {
  const note = figure.querySelector('figcaption');
  if (!(note instanceof HTMLElement)) return;
  note.classList.add(DRAFT_FIGURE_NOTE_CLASS);
  note.classList.remove('qxw-draft-figure__note');
  note.setAttribute('contenteditable', 'false');
}

/** Write / clear description on figure (empty → remove figcaption). */
export function applyDraftFigureNote(figure: HTMLElement, note: string): void {
  const text = note.replace(/\u00a0/g, ' ').trim().slice(0, 2000);
  let el = figure.querySelector('figcaption');
  if (!text) {
    el?.remove();
    return;
  }
  if (!(el instanceof HTMLElement)) {
    el = document.createElement('figcaption');
    el.className = DRAFT_FIGURE_NOTE_CLASS;
    const img = figure.querySelector('img');
    if (img) img.after(el);
    else figure.appendChild(el);
  }
  el.textContent = text;
  normalizeDraftFigureNote(figure);
}

export function readDraftFigureNoteSession(): DraftFigureNoteSession | null {
  if (!noteSession) return null;
  return { quote: noteSession.quote, note: noteSession.note };
}

export function cancelDraftFigureNote(): void {
  noteSession = null;
}

export function commitDraftFigureNote(note: string): boolean {
  if (!noteSession) return false;
  applyDraftFigureNote(noteSession.figure, note);
  noteSession = null;
  return true;
}

/**
 * Open description composer for a figure.
 * Does not invent figcaption until Save with text.
 */
export function beginDraftFigureNote(preferred?: HTMLElement | null): boolean {
  const editor = editorRoot();
  const figure = resolveFigure(preferred);
  if (!editor || !figure) return false;
  selectFigure(editor, figure);
  noteSession = {
    figure,
    quote: quoteFromFigure(figure),
    note: noteTextFromFigure(figure),
  };
  window.dispatchEvent(new CustomEvent(DRAFT_FIGURE_NOTE_EVENT));
  return true;
}

/** Ribbon / panel — Add description under image. */
export function runDraftFigureNote(): boolean {
  return beginDraftFigureNote();
}
